import {createFormHook, createFormHookContexts} from '@tanstack/react-form';
import {Button} from '../../../design-system/atoms/Button/Button';
import {
  ButtonColorScheme,
  ButtonSize,
} from '../../../design-system/atoms/Button/Button.enum';
import {ButtonProps} from '../../../design-system/atoms/Button/ButtonProps.interface';
import {FileFieldV2} from '../../../design-system/molecules/FileFieldV2/FileFieldV2';
import {FileFieldV2Props} from '../../../design-system/molecules/FileFieldV2/FileFieldV2Props.interface';
import {FormGroupV2} from '../../../design-system/molecules/FormGroupV2/FormGroupV2';
import {InputFieldV2} from '../../../design-system/molecules/InputFieldV2/InputFieldV2';
import {InputFieldV2Props} from '../../../design-system/molecules/InputFieldV2/InputFieldV2Props.interface';
import {SelectV2} from '../../../design-system/molecules/SelectV2/SelectV2';
import {SelectV2Props} from '../../../design-system/molecules/SelectV2/SelectV2Props.interface';
import {TextareaFieldV2} from '../../../design-system/molecules/TextareaFieldV2/TextareaFieldV2';
import {TextareaFieldV2Props} from '../../../design-system/molecules/TextareaFieldV2/TextareaFieldV2Props.interface';

const {fieldContext, formContext} = createFormHookContexts();

const {useAppForm} = createFormHook({
  fieldComponents: {
    TextField: (props: {
      isMobile?: boolean;
      label: string;
      subLabel?: string;
      required: boolean;
      inputProps: InputFieldV2Props;
      className?: string;
    }) => (
      <div className={props.className}>
        <FormGroupV2
          isMobile={props.isMobile ?? false}
          status={props.inputProps.status}
          label={props.label}
          subLabel={props.subLabel}
          required={props.required}
        >
          {id => <InputFieldV2 id={id} {...props.inputProps} />}
        </FormGroupV2>
      </div>
    ),
    TextAreaField: (props: {
      isMobile?: boolean;
      label: string;
      subLabel?: string;
      required: boolean;
      currentValue?: number | string;
      maxValue?: number | string;
      textareaProps: TextareaFieldV2Props;
      className?: string;
    }) => (
      <div className={props.className}>
        <FormGroupV2
          isMobile={props.isMobile ?? false}
          status={props.textareaProps.status}
          label={props.label}
          subLabel={props.subLabel}
          required={props.required}
          currentValue={props.currentValue}
          maxValue={props.maxValue}
        >
          {id => <TextareaFieldV2 id={id} {...props.textareaProps} />}
        </FormGroupV2>
      </div>
    ),
    FileUploadField: (props: {
      isMobile?: boolean;
      label: string;
      subLabel?: string;
      currentValue?: number | string;
      maxValue?: number | string;
      fileUploadProps: FileFieldV2Props;
      additionalInfo?: React.ReactNode;
      className?: string;
    }) => (
      <div className={props.className}>
        <FormGroupV2
          isMobile={props.isMobile ?? false}
          status={props.fileUploadProps.status}
          label={props.label}
          subLabel={props.subLabel}
          currentValue={`${props.currentValue}MB`}
          maxValue={`${props.maxValue}MB`}
          additionalInfo={props.additionalInfo}
        >
          {id => <FileFieldV2 id={id} {...props.fileUploadProps} />}
        </FormGroupV2>
      </div>
    ),
    SelectField: (props: {
      isMobile?: boolean;
      label: string;
      required: boolean;
      selectProps: SelectV2Props;
      className?: string;
    }) => (
      <div className={props.className}>
        <FormGroupV2
          isMobile={props.isMobile ?? false}
          status={props.selectProps.status}
          label={props.label}
          required={props.required}
        >
          {id => (
            <SelectV2
              isMobile={props.isMobile}
              id={id}
              {...props.selectProps}
            />
          )}
        </FormGroupV2>
      </div>
    ),
  },
  formComponents: {
    SubmitButton: (props: {
      label: string;
      buttonProps?: Omit<ButtonProps, 'children' | 'size' | 'isRound'>;
      className?: string;
    }) => (
      <div className={props.className}>
        <Button
          type="submit"
          colorScheme={ButtonColorScheme['2nd']}
          className="rounded-none"
          size={ButtonSize.BottomFixed}
          {...props.buttonProps}
        >
          {props.label}
        </Button>
      </div>
    ),
  },
  fieldContext,
  formContext,
});

export {useAppForm as useAskRequestForm};
